import React, { useEffect, useState } from "react";
import { viewAllPost } from "../../api/community.js";
import CardComponent from "./CardComponent.js";
import CommunityHeader from "./communityHeader.js";
import LeftSidebar from "./LeftSidebar.js";
import RightSidebar from "./RightSidebar.js";
import CreatePostModal from "./CreatePostModal.js";
import {
  Sparkles,
  TrendingUp,
  Users,
  Image as ImageIcon,
  BarChart2,
  Calendar,
  Award,
  ChevronDown,
  LayoutGrid,
  List,
  Loader2,
  Filter,
} from "lucide-react";

function Community() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("all");
  const [feedTab, setFeedTab] = useState("all");
  const [sortBy, setSortBy] = useState("latest");
  const [viewMode, setViewMode] = useState("list");
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [postTargetScope, setPostTargetScope] = useState("Public");
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  // Default mock feed matching screenshot
  const initialPosts = [
    {
      _id: "placement_2026",
      title: "Campus Placement Drive 2026 📣",
      description:
        "Great news! Our college is hosting a mega placement drive for the batch of 2026. Top companies will be visiting. Stay tuned for more updates and eligibility criteria.",
      collage: "Bansal Institute of Engineering & Tech",
      timeAgo: "2h ago",
      scope: "College Community",
      bannerStyle: "placement",
      tags: ["Placement", "2026 Batch", "Important"],
      likeCount: 128,
      commentCount: 36,
      owner: {
        name: "Aditya Rai",
        verified: true,
        avatar:
          "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80",
      },
    },
    {
      _id: "hackathon_2025",
      title: "Hackathon 2025 – Register Now! 🚀",
      description:
        "Exciting opportunity for all tech enthusiasts! Showcase your skills, build innovative solutions and win exciting prizes.",
      collage: "Mera College, Pune",
      timeAgo: "5h ago",
      scope: "Public Post",
      tags: ["Hackathon", "Coding", "TechEvent"],
      likeCount: 89,
      commentCount: 19,
      image: {
        url: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800&q=80",
      },
      owner: {
        name: "Sneha Sharma",
        verified: true,
        avatar:
          "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=150&q=80",
      },
    },
  ];

  const getDataFromBackend = async () => {
    try {
      setLoading(true);
      const response = await viewAllPost();
      if (response.data && response.data.posts && response.data.posts.length > 0) {
        setPosts([...response.data.posts, ...initialPosts]);
      } else {
        setPosts(initialPosts);
      }
    } catch (error) {
      console.warn("Backend server not reached, using rich feed:", error);
      setPosts(initialPosts);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getDataFromBackend();
  }, []);

  const handlePostCreated = (newPost) => {
    setPosts((prev) => [newPost, ...prev]);
  };

  // Filter posts based on active tab and community selection
  const filteredPosts = posts.filter((post) => {
    if (activeFilter === "saved") return post.isSaved;
    if (activeFilter === "public")
      return post.scope === "Public Post" || post.isPublic;
    if (activeFilter === "college")
      return post.scope === "College Community" || post.collage;
    if (selectedCommunity) {
      if (selectedCommunity === "bansal")
        return (
          post.collage &&
          post.collage.toLowerCase().includes("bansal")
        );
      if (selectedCommunity === "mera")
        return (
          post.collage &&
          post.collage.toLowerCase().includes("mera")
        );
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-slate-50/70 text-slate-800 pb-16">
      {/* Header Bar */}
      <CommunityHeader onOpenCreateModal={() => setIsCreateModalOpen(true)} />

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {/* Mobile Filter Toggle Button */}
        <div className="lg:hidden mb-4 flex justify-between items-center bg-white p-3 rounded-xl border border-gray-200">
          <span className="text-xs font-bold text-gray-700 uppercase">
            Filters & Communities
          </span>
          <button
            onClick={() => setShowMobileSidebar(!showMobileSidebar)}
            className="px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs font-semibold flex items-center space-x-1"
          >
            <Filter size={14} />
            <span>{showMobileSidebar ? "Hide Controls" : "Show Controls"}</span>
          </button>
        </div>

        {/* Responsive Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Left Sidebar */}
          <div
            className={`lg:col-span-3 ${
              showMobileSidebar ? "block" : "hidden lg:block"
            }`}
          >
            <LeftSidebar
              activeFilter={activeFilter}
              setActiveFilter={setActiveFilter}
              selectedCommunity={selectedCommunity}
              setSelectedCommunity={setSelectedCommunity}
              onOpenCreateModal={() => setIsCreateModalOpen(true)}
            />
          </div>

          {/* Middle Main Feed Column */}
          <main className="lg:col-span-6 space-y-5">
            {/* Create Post Bar (Card matching screenshot) */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100/90">
              <div className="flex items-center space-x-3 pb-3 border-b border-gray-100">
                <img
                  src="https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?auto=format&fit=crop&w=150&q=80"
                  alt="My Profile"
                  className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-100 shrink-0"
                />
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 px-4 py-2.5 rounded-full text-gray-400 text-sm font-medium transition-colors outline-none cursor-pointer"
                >
                  What's on your mind?
                </button>
              </div>

              {/* Quick Actions & Scope Bar */}
              <div className="pt-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center space-x-1 sm:space-x-3 text-xs font-semibold text-gray-600">
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    <ImageIcon size={16} className="text-blue-500" />
                    <span>Photo</span>
                  </button>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    <BarChart2 size={16} className="text-teal-500" />
                    <span>Poll</span>
                  </button>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    <Calendar size={16} className="text-amber-500" />
                    <span>Event</span>
                  </button>
                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center space-x-1.5 px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors text-gray-600"
                  >
                    <Award size={16} className="text-purple-500" />
                    <span>Achievement</span>
                  </button>
                </div>

                <div className="flex items-center space-x-2">
                  <div className="relative">
                    <select
                      value={postTargetScope}
                      onChange={(e) => setPostTargetScope(e.target.value)}
                      className="appearance-none bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1.5 pr-6 rounded-lg cursor-pointer outline-none border border-transparent hover:border-gray-300"
                    >
                      <option value="Public">Post to: Public</option>
                      <option value="College Community">Post to: College</option>
                    </select>
                    <ChevronDown
                      size={14}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
                    />
                  </div>

                  <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="px-5 py-1.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs rounded-xl shadow-xs transition-colors"
                  >
                    Post
                  </button>
                </div>
              </div>
            </div>

            {/* Feed Controls (Feed Tabs, Sort & View Switches) */}
            <div className="flex items-center justify-between flex-wrap gap-3 py-1">
              {/* Tabs */}
              <div className="flex items-center space-x-2 bg-gray-200/50 p-1 rounded-xl">
                <button
                  onClick={() => setFeedTab("all")}
                  className={`flex items-center space-x-1.5 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    feedTab === "all"
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Sparkles size={14} />
                  <span>All Posts</span>
                </button>

                <button
                  onClick={() => setFeedTab("trending")}
                  className={`flex items-center space-x-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    feedTab === "trending"
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <TrendingUp size={14} />
                  <span>Trending</span>
                </button>

                <button
                  onClick={() => setFeedTab("following")}
                  className={`flex items-center space-x-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    feedTab === "following"
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-600 hover:text-gray-900"
                  }`}
                >
                  <Users size={14} />
                  <span>Following</span>
                </button>
              </div>

              {/* Sort & Grid/List View Controls */}
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-1 text-xs text-gray-500 font-medium">
                  <span>Sort by:</span>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="bg-white border border-gray-200 text-gray-800 text-xs font-semibold px-2.5 py-1 rounded-lg outline-none cursor-pointer"
                  >
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                  </select>
                </div>

                <div className="flex items-center space-x-1 bg-white border border-gray-200 rounded-lg p-0.5">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1 rounded ${
                      viewMode === "list"
                        ? "bg-purple-50 text-purple-600 font-bold"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <List size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-1 rounded ${
                      viewMode === "grid"
                        ? "bg-purple-50 text-purple-600 font-bold"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <LayoutGrid size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Posts Stream */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl">
                <Loader2 className="w-10 h-10 text-purple-600 animate-spin mb-3" />
                <p className="text-gray-500 text-sm font-medium">
                  Loading community feed...
                </p>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center border border-gray-100">
                <h4 className="text-lg font-bold text-gray-800 mb-1">
                  No Posts Found
                </h4>
                <p className="text-sm text-gray-500 mb-4">
                  No posts match your selected filter or community.
                </p>
                <button
                  onClick={() => {
                    setActiveFilter("all");
                    setSelectedCommunity(null);
                  }}
                  className="px-4 py-2 bg-purple-600 text-white rounded-xl text-xs font-bold shadow-sm"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 sm:grid-cols-2 gap-4"
                    : "space-y-4"
                }
              >
                {filteredPosts.map((post) => (
                  <CardComponent key={post._id} post={post} />
                ))}
              </div>
            )}
          </main>

          {/* Right Sidebar */}
          <div
            className={`lg:col-span-3 ${
              showMobileSidebar ? "block" : "hidden lg:block"
            }`}
          >
            <RightSidebar />
          </div>
        </div>
      </div>

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onPostCreated={handlePostCreated}
      />
    </div>
  );
}

export default Community;
